import React, { useState } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import './Support.css';

const SupportForm = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    description: '',
    user_email: user?.email || '',
    // Medicinska term-specifika fält
    incorrect_term: '',
    correct_term: '', 
    medical_category: 'global',
    profession: 'alla',
    context: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { value: 'teknisk support', label: 'Teknisk support' },
    { value: 'funktionsfråga', label: 'Funktionsfråga' },
    { value: 'bugg', label: 'Bugg' },
    { value: 'lägg till medicinsk term', label: 'Lägg till medicinsk term' },
    { value: 'annat', label: 'Annat' }
  ];

  const medicalCategories = [
    { value: 'global', label: 'Global' },
    { value: 'medication', label: 'Medicin/Läkemedel' },
    { value: 'diagnosis', label: 'Diagnos' },
    { value: 'test', label: 'Test/Undersökning' },
    { value: 'anatomy', label: 'Anatomi' },
    { value: 'other', label: 'Övrigt' }
  ];

  const professions = [
    { value: 'alla', label: 'Alla' },
    { value: 'doctor', label: 'Läkare' },
    { value: 'physiotherapist', label: 'Sjukgymnast' },
    { value: 'psychologist', label: 'Psykolog' },
    { value: 'nurse', label: 'Sköterska' },
    { value: 'other', label: 'Övrigt' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Om kategorin ändras till medicinsk term, sätt standardvärden
    if (name === 'category' && value === 'lägg till medicinsk term') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        medical_category: 'global',
        profession: 'alla',
        subject: ''  // Clear subject since it won't be used
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate basic fields (subject and description not required for medical terms)
    if (formData.category === 'lägg till medicinsk term') {
      // For medical terms, only validate category, user_email and medical fields
      if (!formData.category || !formData.user_email) {
        toast.error('Vänligen fyll i kategori och e-post');
        return;
      }
      if (!formData.incorrect_term || !formData.correct_term || !formData.medical_category || !formData.profession) {
        toast.error('Vänligen fyll i alla medicinska term-fält');
        return;
      }
    } else {
      // For other categories, validate all basic fields including description and subject
      if (!formData.subject || !formData.category || !formData.description || !formData.user_email) {
        toast.error('Vänligen fyll i alla fält');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/api/support/ticket', formData);
      
      if (response.data.success) {
        toast.success('Din ticket är inskickad! Vi återkommer så snart som möjligt.');
        onClose();
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Kunde inte skicka ticket. Försök igen senare.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-form" onClick={(e) => e.stopPropagation()}>
        <div className="support-form__header">
          <h3>Kontakta Support</h3>
          <button 
            className="support-form__close"
            onClick={onClose}
            aria-label="Stäng"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="support-form__body">
            <div className="form-group">
              <label htmlFor="category">Kategori *</label>
              <select
                id="category"
                name="category"
                className="form-control"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Välj kategori...</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Ämnesfält - visas ej för medicinska termer */}
            {formData.category !== 'lägg till medicinsk term' && (
              <div className="form-group">
                <label htmlFor="subject">Ämne *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="form-control"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Kort beskrivning av ditt ärende"
                  maxLength={200}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="user_email">E-post *</label>
              <input
                type="email"
                id="user_email"
                name="user_email"
                className="form-control"
                value={formData.user_email}
                onChange={handleChange}
                placeholder="Din e-postadress"
                required
              />
              <small className="form-text text-muted">
                Vi skickar en bekräftelse till denna adress
              </small>
            </div>

            {/* Beskrivningsfält - visas ej för medicinska termer */}
            {formData.category !== 'lägg till medicinsk term' && (
              <div className="form-group">
                <label htmlFor="description">Beskrivning *</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Beskriv ditt ärende så detaljerat som möjligt..."
                  rows={6}
                  required
                />
              </div>
            )}

            {/* Medicinska term-fält - visas endast när kategorin är vald */}
            {formData.category === 'lägg till medicinsk term' && (
              <>
                <div className="form-group">
                  <label htmlFor="incorrect_term">Felstavad/Feltranskriberad term *</label>
                  <input
                    type="text"
                    id="incorrect_term"
                    name="incorrect_term"
                    className="form-control"
                    value={formData.incorrect_term}
                    onChange={handleChange}
                    placeholder="Hur termen var felstavad eller feltranskriberad"
                    required
                  />
                  <small className="form-text text-muted">
                    Skriv exakt hur termen blev felstavad i transkriptionen
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="correct_term">Korrekt stavning *</label>
                  <input
                    type="text"
                    id="correct_term"
                    name="correct_term"
                    className="form-control"
                    value={formData.correct_term}
                    onChange={handleChange}
                    placeholder="Hur termen ska stavas korrekt"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="medical_category">Typ av medicinsk term *</label>
                  <select
                    id="medical_category"
                    name="medical_category"
                    className="form-control"
                    value={formData.medical_category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Välj typ...</option>
                    {medicalCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="profession">Profession *</label>
                  <select
                    id="profession"
                    name="profession"
                    className="form-control"
                    value={formData.profession}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Välj profession...</option>
                    {professions.map(prof => (
                      <option key={prof.value} value={prof.value}>
                        {prof.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Vilken profession använder denna term mest?
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="context">Kontext/Sammanhang</label>
                  <textarea
                    id="context"
                    name="context"
                    className="form-control"
                    value={formData.context}
                    onChange={handleChange}
                    placeholder="I vilket sammanhang användes termen? (valfritt)"
                    rows={3}
                  />
                  <small className="form-text text-muted">
                    Beskriv i vilket sammanhang eller vilken typ av anteckning termen användes
                  </small>
                </div>
              </>
            )}
          </div>

          <div className="support-form__footer">
            <button
              type="button"
              className="support-cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#6c757d',
                border: '2px solid #6c757d',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginRight: '10px',
                minWidth: '100px',
                minHeight: '44px'
              }}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="support-submit-button"
              disabled={isSubmitting}
              style={{
                backgroundColor: '#007bff',
                border: '2px solid #007bff',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                minWidth: '120px',
                minHeight: '44px'
              }}
            >
              {isSubmitting ? 'Skickar...' : 'Skicka ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportForm;